// ==================== PANEL DE ADMINISTRACIÓN ====================
// Panel exclusivo para Super Administrador para gestionar usuarios

import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../../servicios/adminService';
import ConfirmDeleteModal from '../compartidos/ConfirmDeleteModal';
import '../../estilos/AdminPanel.css';

const AdminPanel = ({ vistaExterna, ocultarSidebar }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [paginacion, setPaginacion] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');
  
  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('');
  
  // Modal de eliminación
  const [mostrarModal, setMostrarModal] = useState(false);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [estadisticasUsuario, setEstadisticasUsuario] = useState(null);

  // Vista de logs
  const [vistaInterna, setVistaInterna] = useState('dashboard');
  const vistaActual = vistaExterna || vistaInterna;
  const setVistaActual = vistaExterna ? () => {} : setVistaInterna; // Si viene vista externa, no se usa el setter interno
  const [logs, setLogs] = useState([]);

  // Estadísticas generales
  const [estadisticasGenerales, setEstadisticasGenerales] = useState(null);
  const [estadisticasPorProyecto, setEstadisticasPorProyecto] = useState([]);
  
  // Información del super admin actual
  const [infoSuperAdmin, setInfoSuperAdmin] = useState(null);

  // Estados para envío masivo de correos
  const [proyectosSeleccionados, setProyectosSeleccionados] = useState([]);
  const [destinatariosPorProyecto, setDestinatariosPorProyecto] = useState({});
  const [destinatariosSeleccionados, setDestinatariosSeleccionados] = useState([]);
  const [asuntoCorreo, setAsuntoCorreo] = useState('');
  const [mensajeCorreo, setMensajeCorreo] = useState('');
  const [enviandoCorreo, setEnviandoCorreo] = useState(false);
  const [cargandoDestinatarios, setCargandoDestinatarios] = useState({});

  // Estados para plantillas de correo
  const [plantillasCorreo, setPlantillasCorreo] = useState([]);
  const [nombrePlantilla, setNombrePlantilla] = useState('');
  const [guardandoPlantilla, setGuardandoPlantilla] = useState(false);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [mostrarFormGuardar, setMostrarFormGuardar] = useState(false);

  // Estados para gestión de apps por proyecto
  const [actualizandoApps, setActualizandoApps] = useState({});

  // Cargar usuarios
  const cargarUsuarios = useCallback(async () => {
    try {
      setCargando(true);
      setError('');
      
      const data = await adminService.obtenerUsuarios({
        page: paginacion.page,
        limit: paginacion.limit,
        search: busqueda,
        rol: filtroRol,
        activo: filtroActivo
      });

      setUsuarios(data.usuarios);
      setPaginacion(data.paginacion);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, [paginacion.page, paginacion.limit, busqueda, filtroRol, filtroActivo]);

  // Cargar logs
  const cargarLogs = useCallback(async () => {
    try {
      setCargando(true);
      setError('');
      
      const data = await adminService.obtenerLogs({
        page: paginacion.page,
        limit: 20
      });

      setLogs(data.logs);
      setPaginacion(data.paginacion);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, [paginacion.page]);

  // Cargar estadísticas generales
  const cargarEstadisticas = useCallback(async () => {
    try {
      setCargando(true);
      setError('');
      
      const data = await adminService.obtenerEstadisticasGenerales();
      setEstadisticasGenerales(data.estadisticas);
      setEstadisticasPorProyecto(data.proyectosDetalle || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, []);

  // Cargar información del super admin actual
  const cargarInfoSuperAdmin = useCallback(async () => {
    try {
      const data = await adminService.obtenerMiInformacion();
      setInfoSuperAdmin(data);
    } catch (err) {
      console.error('Error al cargar información del super admin:', err);
      // No mostrar error en pantalla, es solo información adicional
    }
  }, []);

  // Efecto para cargar datos
  useEffect(() => {
    if (vistaActual === 'usuarios') {
      cargarUsuarios();
    } else if (vistaActual === 'logs') {
      cargarLogs();
    } else if (vistaActual === 'dashboard') {
      cargarEstadisticas();
      cargarInfoSuperAdmin();
    } else if (vistaActual === 'enviar-correos') {
      // Cargar estadísticas si aún no están
      if (!estadisticasPorProyecto || estadisticasPorProyecto.length === 0) {
        cargarEstadisticas();
      }
      // Cargar plantillas de correo
      cargarPlantillasCorreo();
    } else if (vistaActual === 'apps') {
      if (!estadisticasPorProyecto || estadisticasPorProyecto.length === 0) {
        cargarEstadisticas();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacion.page, filtroRol, filtroActivo, vistaActual, cargarUsuarios, cargarLogs, cargarEstadisticas, cargarInfoSuperAdmin]);

  // Buscar con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (vistaActual === 'usuarios') {
        cargarUsuarios();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [busqueda, vistaActual, cargarUsuarios]);

  // Manejar cambio de página
  const cambiarPagina = (nuevaPagina) => {
    setPaginacion(prev => ({ ...prev, page: nuevaPagina }));
  };

  // Preparar eliminación de usuario
  const prepararEliminacion = async (usuario) => {
    try {
      setCargando(true);
      const data = await adminService.obtenerEstadisticasUsuario(usuario._id);
      setUsuarioAEliminar(usuario);
      setEstadisticasUsuario(data.estadisticas);
      setMostrarModal(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // Confirmar eliminación
  const confirmarEliminacion = async () => {
    try {
      setCargando(true);
      setMostrarModal(false);
      
      const resultado = await adminService.eliminarUsuario(usuarioAEliminar._id);
      
      setExito(`Usuario ${usuarioAEliminar.nombre} eliminado exitosamente. 
        Total de elementos eliminados: ${resultado.estadisticas.total}`);
      setUsuarioAEliminar(null);
      setEstadisticasUsuario(null);
      
      // Recargar lista
      setTimeout(() => {
        cargarUsuarios();
      }, 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // Cancelar eliminación
  const cancelarEliminacion = () => {
    setMostrarModal(false);
    setUsuarioAEliminar(null);
    setEstadisticasUsuario(null);
  };

  // Toggle de app visible para un proyecto
  const toggleAppProyecto = async (proyectoId, appKey, valorActual) => {
    try {
      setActualizandoApps(prev => ({ ...prev, [proyectoId + '-' + appKey]: true }));
      
      await adminService.actualizarAppsVisibles(proyectoId, {
        [appKey]: !valorActual
      });

      // Actualizar estado local
      setEstadisticasPorProyecto(prev => 
        prev.map(p => {
          if (p._id === proyectoId) {
            return {
              ...p,
              appsVisibles: {
                ...p.appsVisibles,
                [appKey]: !valorActual
              }
            };
          }
          return p;
        })
      );

      const appNombres = { gestracoo: 'GestraCOO', recepcion: 'Recepción', programa: 'Programa de Obra', calidad: 'Calidad' };
      const appNombre = appNombres[appKey] || appKey;
      setExito(`App ${appNombre} ${!valorActual ? 'activada' : 'desactivada'} exitosamente`);
      setTimeout(() => setExito(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setActualizandoApps(prev => ({ ...prev, [proyectoId + '-' + appKey]: false }));
    }
  };

  // Toggle activo/inactivo
  const toggleActivo = async (usuario) => {
    try {
      setCargando(true);
      await adminService.toggleActivoUsuario(usuario._id);
      setExito(`Usuario ${usuario.nombre} ${usuario.activo ? 'desactivado' : 'activado'} exitosamente`);
      cargarUsuarios();
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // Formatear fecha
  const formatearFecha = (fecha) => {
    if (!fecha) return 'N/A';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Obtener badge de rol
  const getBadgeRol = (rol) => {
    const badges = {
      superadmin: <span className="badge badge-superadmin">SUPER ADMIN</span>,
      admin: <span className="badge badge-admin">Admin</span>,
      usuario: <span className="badge badge-usuario">Usuario</span>
    };
    return badges[rol] || <span className="badge">{rol}</span>;
  };

  // Obtener badge de acción (logs)
  const getBadgeAccion = (accion) => {
    const badges = {
      eliminar_usuario: <span className="badge badge-eliminar">Eliminar</span>,
      desactivar_usuario: <span className="badge badge-desactivar">Desactivar</span>,
      activar_usuario: <span className="badge badge-activar">Activar</span>,
      modificar_usuario: <span className="badge badge-modificar">Modificar</span>,
      crear_admin: <span className="badge badge-crear">Crear Admin</span>
    };
    return badges[accion] || <span className="badge">{accion}</span>;
  };

  // ==================== FUNCIONES PARA PLANTILLAS DE CORREO ====================

  const cargarPlantillasCorreo = async () => {
    try {
      const data = await adminService.obtenerPlantillasCorreo();
      setPlantillasCorreo(data.plantillas || []);
    } catch (err) {
      console.error('Error al cargar plantillas de correo:', err);
    }
  };

  const guardarPlantillaCorreo = async () => {
    if (!nombrePlantilla.trim()) {
      setError('Ingresa un nombre para la plantilla');
      return;
    }
    if (!asuntoCorreo.trim() || asuntoCorreo.trim().length < 5) {
      setError('El asunto debe tener al menos 5 caracteres para guardar la plantilla');
      return;
    }
    if (!mensajeCorreo.trim() || mensajeCorreo.trim().length < 3) {
      setError('El mensaje debe tener al menos 3 caracteres para guardar la plantilla');
      return;
    }

    try {
      setGuardandoPlantilla(true);
      setError('');

      if (plantillaEditando) {
        await adminService.actualizarPlantillaCorreo(plantillaEditando._id, {
          nombre: nombrePlantilla.trim(),
          asunto: asuntoCorreo.trim(),
          mensaje: mensajeCorreo.trim()
        });
        setExito(`✅ Plantilla "${nombrePlantilla}" actualizada correctamente`);
      } else {
        await adminService.crearPlantillaCorreo({
          nombre: nombrePlantilla.trim(),
          asunto: asuntoCorreo.trim(),
          mensaje: mensajeCorreo.trim()
        });
        setExito(`✅ Plantilla "${nombrePlantilla}" guardada correctamente`);
      }

      setNombrePlantilla('');
      setPlantillaEditando(null);
      setMostrarFormGuardar(false);
      await cargarPlantillasCorreo();
    } catch (err) {
      setError(err.message || 'Error al guardar la plantilla');
    } finally {
      setGuardandoPlantilla(false);
    }
  };

  const cargarPlantillaEnFormulario = async (plantilla) => {
    setAsuntoCorreo(plantilla.asunto);
    setMensajeCorreo(plantilla.mensaje);
    setPlantillaEditando(plantilla);
    setNombrePlantilla(plantilla.nombre);
    setMostrarFormGuardar(false);
    setExito(`📋 Plantilla "${plantilla.nombre}" cargada`);

    // Registrar uso
    try {
      await adminService.usarPlantillaCorreo(plantilla._id);
      await cargarPlantillasCorreo();
    } catch (err) {
      console.error('Error al registrar uso:', err);
    }
  };

  const eliminarPlantillaCorreo = async (plantilla) => {
    const confirmacion = window.confirm(`¿Eliminar la plantilla "${plantilla.nombre}"?\n\nEsta acción no se puede deshacer.`);
    if (!confirmacion) return;

    try {
      await adminService.eliminarPlantillaCorreo(plantilla._id);
      setExito(`🗑️ Plantilla "${plantilla.nombre}" eliminada`);
      if (plantillaEditando && plantillaEditando._id === plantilla._id) {
        setPlantillaEditando(null);
        setNombrePlantilla('');
      }
      await cargarPlantillasCorreo();
    } catch (err) {
      setError(err.message || 'Error al eliminar la plantilla');
    }
  };

  const nuevaPlantillaDesdeVacio = () => {
    setPlantillaEditando(null);
    setNombrePlantilla('');
    setAsuntoCorreo('');
    setMensajeCorreo('');
    setMostrarFormGuardar(false);
  };

  // ==================== FUNCIONES PARA ENVÍO DE CORREOS ====================
  
  // Toggle selección de proyecto
  const toggleProyecto = async (proyectoId) => {
    const yaSeleccionado = proyectosSeleccionados.includes(proyectoId);
    
    if (yaSeleccionado) {
      // Deseleccionar proyecto y sus destinatarios
      setProyectosSeleccionados(prev => prev.filter(id => id !== proyectoId));
      setDestinatariosSeleccionados(prev => prev.filter(d => d.proyectoId !== proyectoId));
    } else {
      // Seleccionar proyecto y cargar destinatarios
      setProyectosSeleccionados(prev => [...prev, proyectoId]);
      
      // Cargar destinatarios si no están cargados
      if (!destinatariosPorProyecto[proyectoId]) {
        try {
          setCargandoDestinatarios(prev => ({ ...prev, [proyectoId]: true }));
          const data = await adminService.obtenerDestinatariosPorProyecto(proyectoId);
          setDestinatariosPorProyecto(prev => ({ ...prev, [proyectoId]: data }));
          
          // Auto-seleccionar todos los destinatarios del proyecto
          const nuevosDestinatarios = [];
          if (data.propietario) {
            nuevosDestinatarios.push({
              id: data.propietario._id,
              email: data.propietario.email,
              nombre: data.propietario.nombre,
              tipo: 'propietario',
              cargo: data.propietario.rol,
              proyectoId
            });
          }
          data.miembros.forEach(m => {
            nuevosDestinatarios.push({
              id: m._id,
              email: m.email,
              nombre: m.nombre,
              tipo: 'miembro',
              cargo: m.cargo,
              proyectoId
            });
          });
          setDestinatariosSeleccionados(prev => [...prev, ...nuevosDestinatarios]);
        } catch (err) {
          setError('Error al cargar destinatarios: ' + err.message);
        } finally {
          setCargandoDestinatarios(prev => ({ ...prev, [proyectoId]: false }));
        }
      } else {
        // Ya cargados, auto-seleccionar todos
        const data = destinatariosPorProyecto[proyectoId];
        const nuevosDestinatarios = [];
        if (data.propietario) {
          nuevosDestinatarios.push({
            id: data.propietario._id,
            email: data.propietario.email,
            nombre: data.propietario.nombre,
            tipo: 'propietario',
            cargo: data.propietario.rol,
            proyectoId
          });
        }
        data.miembros.forEach(m => {
          nuevosDestinatarios.push({
            id: m._id,
            email: m.email,
            nombre: m.nombre,
            tipo: 'miembro',
            cargo: m.cargo,
            proyectoId
          });
        });
        setDestinatariosSeleccionados(prev => [...prev, ...nuevosDestinatarios]);
      }
    }
  };

  // Toggle selección de destinatario individual
  const toggleDestinatario = (destinatario) => {
    const yaSeleccionado = destinatariosSeleccionados.some(
      d => d.id === destinatario.id && d.proyectoId === destinatario.proyectoId
    );
    
    if (yaSeleccionado) {
      setDestinatariosSeleccionados(prev => 
        prev.filter(d => !(d.id === destinatario.id && d.proyectoId === destinatario.proyectoId))
      );
    } else {
      setDestinatariosSeleccionados(prev => [...prev, destinatario]);
    }
  };

  // Seleccionar/deseleccionar todos los destinatarios de un proyecto
  const toggleTodosDelProyecto = (proyectoId) => {
    const data = destinatariosPorProyecto[proyectoId];
    if (!data) return;
    
    const todosDelProyecto = [];
    if (data.propietario) {
      todosDelProyecto.push({
        id: data.propietario._id,
        email: data.propietario.email,
        nombre: data.propietario.nombre,
        tipo: 'propietario',
        cargo: data.propietario.rol,
        proyectoId
      });
    }
    data.miembros.forEach(m => {
      todosDelProyecto.push({
        id: m._id,
        email: m.email,
        nombre: m.nombre,
        tipo: 'miembro',
        cargo: m.cargo,
        proyectoId
      });
    });
    
    const todosSeleccionados = todosDelProyecto.every(td => 
      destinatariosSeleccionados.some(d => d.id === td.id && d.proyectoId === td.proyectoId)
    );
    
    if (todosSeleccionados) {
      // Deseleccionar todos del proyecto
      setDestinatariosSeleccionados(prev => prev.filter(d => d.proyectoId !== proyectoId));
    } else {
      // Seleccionar los que faltan
      const faltantes = todosDelProyecto.filter(td => 
        !destinatariosSeleccionados.some(d => d.id === td.id && d.proyectoId === td.proyectoId)
      );
      setDestinatariosSeleccionados(prev => [...prev, ...faltantes]);
    }
  };

  // Enviar correos masivos
  const enviarCorreoMasivo = async () => {
    if (destinatariosSeleccionados.length === 0) {
      setError('Selecciona al menos un destinatario');
      return;
    }
    if (!asuntoCorreo.trim() || asuntoCorreo.trim().length < 5) {
      setError('El asunto debe tener al menos 5 caracteres');
      return;
    }
    if (!mensajeCorreo.trim() || mensajeCorreo.trim().length < 3) {
      setError('El mensaje debe tener al menos 3 caracteres');
      return;
    }
    
    // Eliminar duplicados por email
    const emailsUnicos = new Map();
    destinatariosSeleccionados.forEach(d => {
      if (!emailsUnicos.has(d.email)) {
        emailsUnicos.set(d.email, d);
      }
    });
    const destinatariosUnicos = Array.from(emailsUnicos.values());
    
    const confirmacion = window.confirm(
      `¿Enviar correo a ${destinatariosUnicos.length} destinatario(s)?\n\nAsunto: ${asuntoCorreo}\n\nEsta acción no se puede deshacer.`
    );
    
    if (!confirmacion) return;
    
    try {
      setEnviandoCorreo(true);
      setError('');
      
      const resultado = await adminService.enviarCorreoPersonalizado({
        destinatarios: destinatariosUnicos.map(d => {
          const proy = estadisticasPorProyecto.find(p => p._id === d.proyectoId);
          return {
            nombre: d.nombre,
            email: d.email,
            tipo: d.tipo,
            cargo: d.cargo || '',
            proyecto: proy?.nombre || ''
          };
        }),
        asunto: asuntoCorreo.trim(),
        mensaje: mensajeCorreo.trim()
      });
      
      const stats = resultado.estadisticas;
      setExito(`✅ Correo enviado: ${stats.exitosos} exitoso(s)${stats.fallidos > 0 ? `, ${stats.fallidos} fallido(s)` : ''}`);
      
      // Limpiar formulario
      setAsuntoCorreo('');
      setMensajeCorreo('');
      setDestinatariosSeleccionados([]);
      setProyectosSeleccionados([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviandoCorreo(false);
    }
  };

  return (
    <div className={`admin-panel${ocultarSidebar ? ' admin-panel-inline' : ''}`}>
      <div className="app-container" style={ocultarSidebar ? { display: 'block', height: 'auto', overflow: 'visible' } : undefined}>
        {/* SIDEBAR CON PESTAÑAS - Solo visible en modo standalone */}
        {!ocultarSidebar && (
          <aside className="sidebar">
            <div className="sidebar-header">
              <div className="sidebar-brand">
                <div className="sidebar-brand-icon">SA</div>
                <div className="sidebar-brand-text">
                  <h2>Panel Admin</h2>
                  <p>Gestión del Sistema</p>
                </div>
              </div>
            </div>

            <div className="tabs-navigation">
              <button
                className={`tab-button ${vistaActual === 'dashboard' ? 'active' : ''}`}
                onClick={() => setVistaActual('dashboard')}
              >
                <span className="tab-icon">D</span>
                Dashboard
              </button>
              <button
                className={`tab-button ${vistaActual === 'usuarios' ? 'active' : ''}`}
                onClick={() => setVistaActual('usuarios')}
              >
                <span className="tab-icon">U</span>
                Usuarios
              </button>
              <button
                className={`tab-button ${vistaActual === 'logs' ? 'active' : ''}`}
                onClick={() => setVistaActual('logs')}
              >
                <span className="tab-icon">L</span>
                Logs de Auditoría
              </button>
              <button
                className={`tab-button ${vistaActual === 'enviar-correos' ? 'active' : ''}`}
                onClick={() => setVistaActual('enviar-correos')}
              >
                <span className="tab-icon">✉</span>
                Enviar Correos
              </button>
              <button
                className={`tab-button ${vistaActual === 'apps' ? 'active' : ''}`}
                onClick={() => setVistaActual('apps')}
              >
                <span className="tab-icon">📱</span>
                Apps por Proyecto
              </button>
            </div>
          </aside>
        )}

        {/* CONTENIDO PRINCIPAL */}
        <main className="main-content" style={ocultarSidebar ? { marginLeft: 0, width: '100%', overflow: 'visible', height: 'auto', padding: '1rem 0' } : undefined}>
          {/* Mensajes */}
          {error && (
            <div className="mensaje-error">
              {error}
              <button onClick={() => setError('')}>×</button>
            </div>
          )}
          {exito && (
            <div className="mensaje-exito">
              {exito}
              <button onClick={() => setExito('')}>×</button>
            </div>
          )}

          {/* Título del panel */}
          {ocultarSidebar && (
            <div style={{ marginBottom: '1.5rem', borderBottom: '2px solid #111827', paddingBottom: '12px' }}>
              <h1 style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#111827' }}>Panel de Administración</h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#6b7280' }}>Gestión del Sistema</p>
            </div>
          )}

          {/* Vista Dashboard */}
          {vistaActual === 'dashboard' && (
            <div className="tab-content">
              <h2 className="section-title">
                Estadísticas del Sistema
              </h2>

              {/* SECCIÓN: Información del Super Admin */}
              {infoSuperAdmin && (
                <>
                  <div className="admin-info-card">
                    <div className="admin-info-header">
                      <div className="admin-info-header-icon">
                        {(infoSuperAdmin.usuario?.nombre || 'SA').charAt(0).toUpperCase()}
                      </div>
                      <h3>Mi Cuenta — {infoSuperAdmin.usuario?.nombre || 'Super Admin'}</h3>
                    </div>
                    <div className="admin-info-details">
                      <div className="admin-info-item">
                        <strong>Email:</strong> {infoSuperAdmin.usuario?.email || 'N/A'}
                      </div>
                      <div className="admin-info-item">
                        <strong>Rol:</strong>{' '}
                        <span className="admin-info-role-badge">
                          {infoSuperAdmin.usuario?.rol?.toUpperCase() || 'N/A'}
                        </span>
                      </div>
                      {infoSuperAdmin.proyecto ? (
                        <>
                          <div className="admin-info-item">
                            <strong>Proyecto:</strong> {infoSuperAdmin.proyecto.nombre}
                          </div>
                          <div className="admin-info-item">
                            <strong>Manager:</strong> {infoSuperAdmin.proyecto.manager || 'N/A'}
                          </div>
                          {infoSuperAdmin.proyecto.direccion && (
                            <div className="admin-info-item">
                              <strong>Dirección:</strong> {infoSuperAdmin.proyecto.direccion}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="admin-info-item" style={{ fontStyle: 'italic', color: '#9ca3af' }}>
                          No hay proyecto asociado
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tabla de miembros del proyecto */}
                  {infoSuperAdmin.miembros && infoSuperAdmin.miembros.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h3 className="section-subtitle">
                        Miembros de Mi Equipo ({infoSuperAdmin.totalMiembros})
                      </h3>
                      <div className="tabla-contenedor">
                        <table className="tabla-usuarios">
                          <thead>
                            <tr>
                              <th>Nombre</th>
                              <th>Email</th>
                              <th>Cargo</th>
                              <th>Teléfono</th>
                              <th>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {infoSuperAdmin.miembros.map((miembro) => (
                              <tr key={miembro._id}>
                                <td style={{ fontWeight: '600' }}>{miembro.nombre}</td>
                                <td>{miembro.email || 'N/A'}</td>
                                <td>{miembro.cargo || 'N/A'}</td>
                                <td>{miembro.telefono || 'N/A'}</td>
                                <td>
                                  {miembro.activo ? (
                                    <span className="badge badge-green">Activo</span>
                                  ) : (
                                    <span className="badge badge-red">Inactivo</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                </>
              )}

              {cargando ? (
                <div className="cargando"><span className="loading-spinner"></span>Cargando estadísticas...</div>
              ) : estadisticasGenerales ? (
                <>
                  {/* Tabla de estadísticas por proyecto */}
                  <h3 className="section-subtitle" style={{ marginTop: '32px' }}>
                    Estadísticas por Proyecto
                  </h3>

                  {estadisticasPorProyecto && estadisticasPorProyecto.length > 0 ? (
                    <div className="tabla-contenedor">
                      <table className="tabla-usuarios">
                        <thead>
                          <tr>
                            <th>Proyecto</th>
                            <th>Gerente</th>
                            <th>Responsable</th>
                            <th>Email</th>
                            <th>Miembros</th>
                            <th>Último Acceso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {estadisticasPorProyecto.map((proyecto, index) => (
                            <tr key={proyecto._id || index}>
                              <td style={{ fontWeight: '600' }}>{proyecto.nombre}</td>
                              <td>{proyecto.manager}</td>
                              <td style={{ fontWeight: '500' }}>{proyecto.responsable?.nombre || 'N/A'}</td>
                              <td>{proyecto.responsable?.email || 'N/A'}</td>
                              <td>
                                <span className="badge badge-teal">{proyecto.stats?.totalMiembros || 0}</span>
                              </td>
                              <td>{formatearFecha(proyecto.ultimoAcceso)}</td>
                            </tr>
                          ))}
                          {/* Fila de totales */}
                          <tr className="totals-row">
                            <td colSpan="4" className="totals-label">
                              TOTALES GENERALES
                            </td>
                            <td>
                              <span className="totals-value">
                                {estadisticasPorProyecto.reduce((sum, proyecto) => sum + (proyecto.stats?.totalMiembros || 0), 0)}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center' }}>—</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="sin-datos">No hay datos disponibles</div>
                  )}
                </>
              ) : (
                <div className="sin-datos">No se pudieron cargar las estadísticas</div>
              )}
            </div>
          )}

          {/* Vista de Usuarios */}
          {vistaActual === 'usuarios' && (
            <div className="tab-content">
              {/* Filtros */}
          <div className="filtros-contenedor">
            <div className="filtro-busqueda">
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="input-busqueda"
              />
            </div>
            
            <div className="filtros-row">
              <select
                value={filtroRol}
                onChange={(e) => setFiltroRol(e.target.value)}
                className="filtro-select"
              >
                <option value="">Todos los roles</option>
                <option value="superadmin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="usuario">Usuario</option>
              </select>

              <select
                value={filtroActivo}
                onChange={(e) => setFiltroActivo(e.target.value)}
                className="filtro-select"
              >
                <option value="">Todos los estados</option>
                <option value="true">Activos</option>
                <option value="false">Inactivos</option>
              </select>

              <button onClick={cargarUsuarios} className="btn-refrescar">
                Refrescar
              </button>
            </div>
          </div>

          {/* Tabla de usuarios */}
          {cargando ? (
            <div className="cargando"><span className="loading-spinner"></span>Cargando usuarios...</div>
          ) : usuarios.length === 0 ? (
            <div className="sin-datos">No se encontraron usuarios</div>
          ) : (
            <>
              {/* Agrupar usuarios por constructora (propietario) → proyecto */}
              {(() => {
                const tablaUsuarios = (usuariosLista) => (
                  <div className="tabla-contenedor">
                    <table className="tabla-usuarios">
                      <thead>
                        <tr>
                          <th>Nombre</th>
                          <th>Email</th>
                          <th>Tipo</th>
                          <th>Rol</th>
                          <th>Estado</th>
                          <th>Fecha Registro</th>
                          <th>Último Acceso</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usuariosLista.map(usuario => (
                          <tr key={usuario._id}>
                            <td>{usuario.nombre}</td>
                            <td>{usuario.email}</td>
                            <td>
                              <span className={`badge ${usuario.tipoUsuario === 'propietario' ? 'badge-blue' : 'badge-purple'}`}>
                                {usuario.tipoUsuario === 'propietario' ? 'Propietario' : 'Miembro'}
                              </span>
                            </td>
                            <td>{getBadgeRol(usuario.rol)}</td>
                            <td>
                              <span className={`estado ${usuario.activo ? 'activo' : 'inactivo'}`}>
                                {usuario.activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td>{formatearFecha(usuario.createdAt)}</td>
                            <td>{formatearFecha(usuario.ultimoAcceso)}</td>
                            <td>
                              <div className="acciones">
                                {usuario.rol !== 'superadmin' && (
                                  <>
                                    <button
                                      onClick={() => toggleActivo(usuario)}
                                      className="btn-accion btn-toggle"
                                      title={usuario.activo ? 'Desactivar' : 'Activar'}
                                    >
                                      {usuario.activo ? '✕' : '✓'}
                                    </button>
                                    <button
                                      onClick={() => prepararEliminacion(usuario)}
                                      className="btn-accion btn-eliminar"
                                      title="Eliminar"
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                                {usuario.rol === 'superadmin' && (
                                  <span className="protegido">Protegido</span>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );

                // Nivel 1: constructora (propietario). Nivel 2: proyectos de esa constructora.
                const constructoras = {};
                const superAdmins = [];
                const sinConstructora = { nombre: 'Sin Constructora Asignada', proyectos: {} };

                const agregarAProyecto = (grupoProyectos, proyKey, proyNombre, usuario) => {
                  if (!grupoProyectos[proyKey]) grupoProyectos[proyKey] = { nombre: proyNombre, usuarios: [] };
                  grupoProyectos[proyKey].usuarios.push(usuario);
                };

                usuarios.forEach(usuario => {
                  if (usuario.rol === 'superadmin') {
                    superAdmins.push(usuario);
                    return;
                  }

                  // Propietario: la constructora es él mismo; sus proyectos son los de él
                  if (usuario.tipoUsuario === 'propietario' || !usuario.tipoUsuario) {
                    const ctKey = usuario._id;
                    if (!constructoras[ctKey]) constructoras[ctKey] = { nombre: usuario.nombre, proyectos: {} };

                    if (usuario.proyectos?.length > 0) {
                      usuario.proyectos.forEach(p => {
                        agregarAProyecto(constructoras[ctKey].proyectos, p._id?.toString() || p.nombre, p.nombre, usuario);
                      });
                    } else {
                      agregarAProyecto(constructoras[ctKey].proyectos, 'sin-proyecto', 'Sin Proyecto Asignado', usuario);
                    }
                    return;
                  }

                  // Miembro: la constructora es el propietario del proyecto al que pertenece
                  if (usuario.tipoUsuario === 'miembro' && usuario.proyecto) {
                    const propietario = usuario.proyecto.propietario;
                    if (propietario) {
                      const ctKey = propietario._id;
                      if (!constructoras[ctKey]) constructoras[ctKey] = { nombre: propietario.nombre, proyectos: {} };
                      agregarAProyecto(constructoras[ctKey].proyectos, usuario.proyecto._id?.toString() || usuario.proyecto.nombre, usuario.proyecto.nombre, usuario);
                    } else {
                      agregarAProyecto(sinConstructora.proyectos, usuario.proyecto._id?.toString() || usuario.proyecto.nombre, usuario.proyecto.nombre, usuario);
                    }
                    return;
                  }

                  // Miembro sin proyecto asociado
                  agregarAProyecto(sinConstructora.proyectos, 'sin-proyecto', 'Sin Proyecto Asignado', usuario);
                });

                const constructorasOrdenadas = Object.entries(constructoras).sort(([, a], [, b]) =>
                  a.nombre.localeCompare(b.nombre)
                );

                return (
                  <>
                    {superAdmins.length > 0 && (
                      <div className="grupo-proyecto">
                        <div className="grupo-proyecto-header">
                          <span>Super Administradores</span>
                          <span className="grupo-proyecto-count">
                            {superAdmins.length} {superAdmins.length === 1 ? 'usuario' : 'usuarios'}
                          </span>
                        </div>
                        {tablaUsuarios(superAdmins)}
                      </div>
                    )}

                    {constructorasOrdenadas.map(([ctKey, constructora]) => {
                      const totalUsuarios = Object.values(constructora.proyectos).reduce((sum, p) => sum + p.usuarios.length, 0);
                      const proyectosOrdenados = Object.entries(constructora.proyectos).sort(([, a], [, b]) =>
                        a.nombre.localeCompare(b.nombre)
                      );
                      return (
                        <div key={ctKey} className="grupo-constructora">
                          <div className="grupo-constructora-header">
                            <span>🏗️ {constructora.nombre}</span>
                            <span className="grupo-proyecto-count">
                              {totalUsuarios} {totalUsuarios === 1 ? 'usuario' : 'usuarios'} · {proyectosOrdenados.length} {proyectosOrdenados.length === 1 ? 'proyecto' : 'proyectos'}
                            </span>
                          </div>
                          {proyectosOrdenados.map(([proyKey, grupo]) => (
                            <div key={proyKey} className="grupo-proyecto grupo-proyecto-anidado">
                              <div className="grupo-proyecto-header">
                                <span>{grupo.nombre}</span>
                                <span className="grupo-proyecto-count">
                                  {grupo.usuarios.length} {grupo.usuarios.length === 1 ? 'usuario' : 'usuarios'}
                                </span>
                              </div>
                              {tablaUsuarios(grupo.usuarios)}
                            </div>
                          ))}
                        </div>
                      );
                    })}

                    {Object.keys(sinConstructora.proyectos).length > 0 && (
                      <div className="grupo-constructora">
                        <div className="grupo-constructora-header">
                          <span>{sinConstructora.nombre}</span>
                        </div>
                        {Object.entries(sinConstructora.proyectos).map(([proyKey, grupo]) => (
                          <div key={proyKey} className="grupo-proyecto grupo-proyecto-anidado">
                            <div className="grupo-proyecto-header">
                              <span>{grupo.nombre}</span>
                              <span className="grupo-proyecto-count">
                                {grupo.usuarios.length} {grupo.usuarios.length === 1 ? 'usuario' : 'usuarios'}
                              </span>
                            </div>
                            {tablaUsuarios(grupo.usuarios)}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}
            </div>
          )}

          {/* Vista de Logs */}
          {vistaActual === 'logs' && (
            <div className="tab-content">
        <div className="logs-contenedor">
          {cargando ? (
            <div className="cargando"><span className="loading-spinner"></span>Cargando logs...</div>
          ) : logs.length === 0 ? (
            <div className="sin-datos">No hay logs registrados</div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla-logs">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Acción</th>
                    <th>Admin</th>
                    <th>Usuario Afectado</th>
                    <th>Detalles</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log._id}>
                      <td>{formatearFecha(log.fecha)}</td>
                      <td>{getBadgeAccion(log.accion)}</td>
                      <td>
                        {log.admin ? `${log.admin.nombre} (${log.admin.email})` : 'N/A'}
                      </td>
                      <td>
                        {log.usuarioAfectado ? `${log.usuarioAfectado.nombre} (${log.usuarioAfectado.email})` : 'N/A'}
                      </td>
                      <td>
                        <details>
                          <summary>Ver más</summary>
                          <pre>{JSON.stringify(log.detalles, null, 2)}</pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
              </div>
            </div>
          )}

          {/* Vista Enviar Correos */}
          {vistaActual === 'enviar-correos' && (
            <div className="tab-content">
              <h2 className="section-title">Envío de Correos</h2>
              <p className="correo-descripcion">Envía avisos y novedades a los usuarios de tus proyectos.</p>

              {/* ===== BARRA DE PLANTILLAS (simplificada) ===== */}
              <div className="plantillas-correo-bar">
                <select
                  className="plantillas-correo-select"
                  value={plantillaEditando?._id || ''}
                  onChange={(e) => {
                    const p = plantillasCorreo.find(pl => pl._id === e.target.value);
                    if (p) cargarPlantillaEnFormulario(p);
                  }}
                >
                  <option value="">{plantillasCorreo.length === 0 ? 'Sin plantillas guardadas' : 'Cargar plantilla...'}</option>
                  {plantillasCorreo.map(p => (
                    <option key={p._id} value={p._id}>{p.nombre}</option>
                  ))}
                </select>

                {plantillaEditando && (
                  <>
                    <span className="plantillas-correo-editando">Editando: <strong>{plantillaEditando.nombre}</strong></span>
                    <button type="button" className="plantillas-correo-link" onClick={() => eliminarPlantillaCorreo(plantillaEditando)}>
                      Eliminar
                    </button>
                    <button type="button" className="plantillas-correo-link" onClick={nuevaPlantillaDesdeVacio}>
                      Nuevo correo
                    </button>
                  </>
                )}

                {!mostrarFormGuardar ? (
                  <button
                    type="button"
                    className="plantillas-correo-link"
                    onClick={() => setMostrarFormGuardar(true)}
                    disabled={asuntoCorreo.trim().length < 5 || mensajeCorreo.trim().length < 3}
                    title={asuntoCorreo.trim().length < 5 || mensajeCorreo.trim().length < 3 ? 'Escribe un asunto y mensaje primero' : ''}
                  >
                    + Guardar como plantilla
                  </button>
                ) : (
                  <div className="plantillas-correo-guardar-inline">
                    <input
                      type="text"
                      value={nombrePlantilla}
                      onChange={(e) => setNombrePlantilla(e.target.value)}
                      placeholder="Nombre de la plantilla"
                      maxLength={100}
                      className="plantillas-correo-input-nombre"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="plantillas-correo-link fuerte"
                      onClick={guardarPlantillaCorreo}
                      disabled={guardandoPlantilla || !nombrePlantilla.trim()}
                    >
                      {guardandoPlantilla ? 'Guardando...' : (plantillaEditando ? 'Actualizar' : 'Guardar')}
                    </button>
                    <button type="button" className="plantillas-correo-cerrar" onClick={() => setMostrarFormGuardar(false)}>×</button>
                  </div>
                )}
              </div>

              <div className="correo-layout">
                {/* PASO 1: Selección de Proyectos */}
                <div className="correo-paso">
                  <div className="correo-paso-header">
                    <span className="correo-paso-numero">1</span>
                    <h3>Seleccionar Proyectos</h3>
                  </div>
                  
                  {!estadisticasPorProyecto || estadisticasPorProyecto.length === 0 ? (
                    <div className="cargando"><span className="loading-spinner"></span>Cargando proyectos...</div>
                  ) : (
                    <div className="correo-proyectos-lista">
                      {estadisticasPorProyecto.map(proy => (
                        <label 
                          key={proy._id} 
                          className={`correo-proyecto-item ${proyectosSeleccionados.includes(proy._id) ? 'selected' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={proyectosSeleccionados.includes(proy._id)}
                            onChange={() => toggleProyecto(proy._id)}
                            className="correo-checkbox"
                          />
                          <div className="correo-proyecto-info">
                            <span className="correo-proyecto-nombre">{proy.nombre}</span>
                            <span className="correo-proyecto-meta">
                              {proy.manager || 'Sin gerente'} · {proy.responsable?.email || 'Sin email'} · {proy.stats?.totalMiembros || 0} miembros
                            </span>
                          </div>
                          {cargandoDestinatarios[proy._id] && (
                            <span className="loading-spinner-small"></span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* PASO 2: Selección de Destinatarios */}
                <div className="correo-paso">
                  <div className="correo-paso-header">
                    <span className="correo-paso-numero">2</span>
                    <h3>Seleccionar Destinatarios</h3>
                    {destinatariosSeleccionados.length > 0 && (
                      <span className="correo-contador-badge">
                        {new Map(destinatariosSeleccionados.map(d => [d.email, d])).size} seleccionados
                      </span>
                    )}
                  </div>

                  {proyectosSeleccionados.length === 0 ? (
                    <div className="correo-placeholder">
                      Selecciona al menos un proyecto para ver sus destinatarios
                    </div>
                  ) : (
                    <div className="correo-destinatarios-lista">
                      {proyectosSeleccionados.map(proyId => {
                        const data = destinatariosPorProyecto[proyId];
                        const proy = estadisticasPorProyecto.find(p => p._id === proyId);
                        
                        if (!data) return (
                          <div key={proyId} className="cargando"><span className="loading-spinner"></span>Cargando...</div>
                        );
                        
                        const todosDelProyecto = [];
                        if (data.propietario) todosDelProyecto.push(data.propietario);
                        todosDelProyecto.push(...data.miembros);
                        
                        const todosSeleccionados = todosDelProyecto.every(td => 
                          destinatariosSeleccionados.some(d => d.id === td._id && d.proyectoId === proyId)
                        );
                        
                        return (
                          <div key={proyId} className="correo-proyecto-grupo">
                            <div className="correo-grupo-header">
                              <strong>{proy?.nombre || 'Proyecto'}</strong>
                              <button 
                                type="button" 
                                className="correo-btn-toggle-all"
                                onClick={() => toggleTodosDelProyecto(proyId)}
                              >
                                {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                              </button>
                            </div>
                            
                            {data.propietario && (
                              <label className={`correo-destinatario-item ${
                                destinatariosSeleccionados.some(d => d.id === data.propietario._id && d.proyectoId === proyId) ? 'selected' : ''
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={destinatariosSeleccionados.some(d => d.id === data.propietario._id && d.proyectoId === proyId)}
                                  onChange={() => toggleDestinatario({
                                    id: data.propietario._id,
                                    email: data.propietario.email,
                                    nombre: data.propietario.nombre,
                                    tipo: 'propietario',
                                    cargo: data.propietario.rol,
                                    proyectoId: proyId
                                  })}
                                  className="correo-checkbox"
                                />
                                <div className="correo-dest-info">
                                  <span className="correo-dest-nombre">{data.propietario.nombre}</span>
                                  <span className="correo-dest-email">{data.propietario.email}</span>
                                </div>
                                <span className="correo-badge-rol">Propietario</span>
                              </label>
                            )}
                            
                            {data.miembros.map(miembro => (
                              <label key={miembro._id} className={`correo-destinatario-item ${
                                destinatariosSeleccionados.some(d => d.id === miembro._id && d.proyectoId === proyId) ? 'selected' : ''
                              }`}>
                                <input
                                  type="checkbox"
                                  checked={destinatariosSeleccionados.some(d => d.id === miembro._id && d.proyectoId === proyId)}
                                  onChange={() => toggleDestinatario({
                                    id: miembro._id,
                                    email: miembro.email,
                                    nombre: miembro.nombre,
                                    tipo: 'miembro',
                                    cargo: miembro.cargo,
                                    proyectoId: proyId
                                  })}
                                  className="correo-checkbox"
                                />
                                <div className="correo-dest-info">
                                  <span className="correo-dest-nombre">{miembro.nombre}</span>
                                  <span className="correo-dest-email">{miembro.email}</span>
                                </div>
                                <span className="correo-badge-rol">{miembro.cargo}</span>
                              </label>
                            ))}
                            
                            {!data.propietario && data.miembros.length === 0 && (
                              <div className="correo-placeholder">Sin destinatarios disponibles</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* PASO 3: Redactar Correo */}
                <div className="correo-paso">
                  <div className="correo-paso-header">
                    <span className="correo-paso-numero">3</span>
                    <h3>Redactar Correo</h3>
                  </div>
                  
                  <div className="correo-formulario">
                    <div className="correo-campo">
                      <label className="correo-label">Asunto</label>
                      <input
                        type="text"
                        value={asuntoCorreo}
                        onChange={(e) => setAsuntoCorreo(e.target.value)}
                        placeholder="Ej: Bienvenido a GestraCOO, {Nombre}"
                        maxLength={100}
                        className="correo-input"
                      />
                      <span className="correo-char-count">{asuntoCorreo.length}/100</span>
                    </div>
                    
                    {/* Variables dinámicas: fila simple de chips */}
                    <div className="correo-variables-lista">
                      <span className="correo-variables-hint">Variables:</span>
                      {[
                        { var: '{Nombre}', desc: 'Nombre completo', ejemplo: 'Benjamin Riveros' },
                        { var: '{Email}', desc: 'Email', ejemplo: 'benjamin4riveros@gmail.com' },
                        { var: '{Proyecto}', desc: 'Proyecto', ejemplo: 'Megaproyecto F1A' },
                        { var: '{Cargo}', desc: 'Cargo o rol', ejemplo: 'Administrador' },
                      ].map(v => (
                        <button
                          key={v.var}
                          type="button"
                          className="correo-variable-chip"
                          title={`${v.desc} — Ej: ${v.ejemplo}`}
                          onClick={() => {
                            setMensajeCorreo(prev => {
                              const textarea = document.querySelector('.correo-textarea');
                              if (textarea) {
                                const start = textarea.selectionStart;
                                const end = textarea.selectionEnd;
                                const newText = prev.substring(0, start) + v.var + prev.substring(end);
                                setTimeout(() => {
                                  textarea.focus();
                                  textarea.setSelectionRange(start + v.var.length, start + v.var.length);
                                }, 0);
                                return newText;
                              }
                              return prev + v.var;
                            });
                          }}
                        >
                          {v.var}
                        </button>
                      ))}
                    </div>

                    <div className="correo-campo">
                      <label className="correo-label">Mensaje</label>
                      <textarea
                        value={mensajeCorreo}
                        onChange={(e) => setMensajeCorreo(e.target.value)}
                        placeholder={`Estimado/a {Nombre},\n\nLa implementación de la plataforma GestraCOO fue completada.\n\nUsuario: {Email}\nProyecto: {Proyecto}\n\nSaludos,\nEquipo GestraCOO`}
                        maxLength={2000}
                        rows={10}
                        className="correo-textarea"
                      />
                      <span className="correo-char-count">{mensajeCorreo.length}/2000</span>
                    </div>

                    {/* Vista previa */}
                    {mensajeCorreo.trim() && destinatariosSeleccionados.length > 0 && (
                      <div className="correo-preview">
                        <div className="correo-preview-header">
                          <span>👁️ Vista previa</span>
                          <span className="correo-preview-dest">para: {destinatariosSeleccionados[0]?.nombre}</span>
                        </div>
                        <div className="correo-preview-body">
                          {(() => {
                            const d = destinatariosSeleccionados[0];
                            const proy = estadisticasPorProyecto.find(p => p._id === d?.proyectoId);
                            return mensajeCorreo
                              .replace(/\{Nombre\}/gi, d?.nombre || '')
                              .replace(/\{Email\}/gi, d?.email || '')
                              .replace(/\{Proyecto\}/gi, proy?.nombre || '')
                              .replace(/\{Cargo\}/gi, d?.cargo || '')
                              .split('\n').map((line, i) => (
                                <span key={i}>{line}<br/></span>
                              ));
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resumen y Enviar */}
                <div className="correo-resumen">
                  <p className="correo-resumen-linea">
                    {proyectosSeleccionados.length} {proyectosSeleccionados.length === 1 ? 'proyecto' : 'proyectos'}
                    {' · '}
                    {new Map(destinatariosSeleccionados.map(d => [d.email, d])).size} destinatarios
                    {' · '}
                    Mensaje {asuntoCorreo.trim().length >= 5 && mensajeCorreo.trim().length >= 3 ? 'listo ✓' : 'incompleto'}
                  </p>

                  <button
                    onClick={enviarCorreoMasivo}
                    disabled={enviandoCorreo || destinatariosSeleccionados.length === 0 || asuntoCorreo.trim().length < 5 || mensajeCorreo.trim().length < 3}
                    className={`correo-btn-enviar ${enviandoCorreo ? 'enviando' : ''}`}
                  >
                    {enviandoCorreo ? (
                      <><span className="loading-spinner"></span> Enviando correos...</>
                    ) : (
                      `Enviar Correo a ${new Map(destinatariosSeleccionados.map(d => [d.email, d])).size} destinatario(s)`
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Vista de Apps por Proyecto */}
          {vistaActual === 'apps' && (
            <div className="tab-content">
              <h2 className="section-title">
                Apps por Proyecto
              </h2>
              <p style={{ color: '#6b7280', marginBottom: '24px', fontSize: '14px' }}>
                Configura qué aplicaciones puede ver cada proyecto. Los cambios se aplican inmediatamente.
              </p>

              {estadisticasPorProyecto && estadisticasPorProyecto.length > 0 ? (
                <div className="tabla-contenedor">
                  <table className="tabla-usuarios">
                    <thead>
                      <tr>
                        <th>Proyecto</th>
                        <th>Gerente</th>
                        <th>Responsable</th>
                        <th style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{ 
                              padding: '2px 8px',
                              background: '#111827',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '800',
                              letterSpacing: '1px'
                            }}>SUBCONTRATOS Y MATERIALES</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Subcontratos y Materiales</span>
                          </div>
                        </th>
                        <th style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              padding: '2px 8px',
                              background: '#111827',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '800',
                              letterSpacing: '1px'
                            }}>RECEPCIÓN</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Recepción</span>
                          </div>
                        </th>
                        <th style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              padding: '2px 8px',
                              background: '#111827',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '800',
                              letterSpacing: '1px'
                            }}>PROGRAMA</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Programa de Obra</span>
                          </div>
                        </th>
                        <th style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                            <span style={{
                              padding: '2px 8px',
                              background: '#111827',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '800',
                              letterSpacing: '1px'
                            }}>CALIDAD</span>
                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Calidad</span>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...estadisticasPorProyecto]
                        .sort((a, b) => {
                          const nombreA = a.responsable?.nombre || 'Sin responsable';
                          const nombreB = b.responsable?.nombre || 'Sin responsable';
                          const cmp = nombreA.localeCompare(nombreB);
                          return cmp !== 0 ? cmp : a.nombre.localeCompare(b.nombre);
                        })
                        .map((proyecto, index, arr) => {
                        const apps = proyecto.appsVisibles || { gestracoo: true, recepcion: true, programa: true, calidad: true };
                        const nombreConstructora = proyecto.responsable?.nombre || 'Sin responsable';
                        const esNuevaConstructora = index === 0 || (arr[index - 1].responsable?.nombre || 'Sin responsable') !== nombreConstructora;
                        const btnStyle = (activo, cargando) => ({
                          padding: '8px 20px',
                          borderRadius: '20px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '700',
                          fontSize: '12px',
                          transition: 'all 0.3s ease',
                          background: activo
                            ? '#111827'
                            : '#e5e7eb',
                          color: activo ? 'white' : '#9ca3af',
                          boxShadow: activo
                            ? '0 2px 8px rgba(0, 0, 0, 0.25)'
                            : 'none',
                          opacity: cargando ? 0.5 : 1
                        });
                        return (
                          <React.Fragment key={proyecto._id || index}>
                          {esNuevaConstructora && (
                            <tr className="grupo-constructora-row">
                              <td colSpan={7} style={{
                                background: '#f3f4f6',
                                fontWeight: '800',
                                fontSize: '12px',
                                letterSpacing: '0.5px',
                                color: '#374151',
                                padding: '10px 12px',
                                textTransform: 'uppercase'
                              }}>
                                {nombreConstructora}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td style={{ fontWeight: '600' }}>{proyecto.nombre}</td>
                            <td>{proyecto.manager}</td>
                            <td>{proyecto.responsable?.nombre || 'N/A'}</td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => toggleAppProyecto(proyecto._id, 'gestracoo', apps.gestracoo)}
                                disabled={actualizandoApps[proyecto._id + '-gestracoo']}
                                style={btnStyle(apps.gestracoo, actualizandoApps[proyecto._id + '-gestracoo'])}
                              >
                                {actualizandoApps[proyecto._id + '-gestracoo']
                                  ? '...'
                                  : apps.gestracoo ? '✓ Activa' : '✗ Inactiva'}
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => toggleAppProyecto(proyecto._id, 'recepcion', apps.recepcion)}
                                disabled={actualizandoApps[proyecto._id + '-recepcion']}
                                style={btnStyle(apps.recepcion, actualizandoApps[proyecto._id + '-recepcion'])}
                              >
                                {actualizandoApps[proyecto._id + '-recepcion']
                                  ? '...'
                                  : apps.recepcion ? '✓ Activa' : '✗ Inactiva'}
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => toggleAppProyecto(proyecto._id, 'programa', apps.programa)}
                                disabled={actualizandoApps[proyecto._id + '-programa']}
                                style={btnStyle(apps.programa ?? true, actualizandoApps[proyecto._id + '-programa'])}
                              >
                                {actualizandoApps[proyecto._id + '-programa']
                                  ? '...'
                                  : (apps.programa ?? true) ? '✓ Activa' : '✗ Inactiva'}
                              </button>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => toggleAppProyecto(proyecto._id, 'calidad', apps.calidad ?? true)}
                                disabled={actualizandoApps[proyecto._id + '-calidad']}
                                style={btnStyle(apps.calidad ?? true, actualizandoApps[proyecto._id + '-calidad'])}
                              >
                                {actualizandoApps[proyecto._id + '-calidad']
                                  ? '...'
                                  : (apps.calidad ?? true) ? '✓ Activa' : '✗ Inactiva'}
                              </button>
                            </td>
                          </tr>
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="sin-datos">
                  {cargando ? 'Cargando proyectos...' : 'No hay proyectos disponibles'}
                </div>
              )}

              <div style={{
                marginTop: '24px',
                padding: '16px',
                background: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                fontSize: '13px',
                color: '#374151',
                lineHeight: '1.6'
              }}>
                <strong>ℹ️ Nota:</strong> Al desactivar un módulo, los usuarios del proyecto dejarán de ver
                las pestañas correspondientes en su sidebar. Los datos <strong>no se eliminan</strong>, solo se ocultan.
                Los cambios se aplican la próxima vez que el usuario recargue la página.
              </div>
            </div>
          )}

          {/* Paginación */}
      {paginacion.totalPages > 1 && (
        <div className="paginacion">
          <button
            onClick={() => cambiarPagina(paginacion.page - 1)}
            disabled={paginacion.page === 1}
            className="btn-paginacion"
          >
            ← Anterior
          </button>
          
          <span className="info-paginacion">
            Página {paginacion.page} de {paginacion.totalPages}
            {' '}({paginacion.total} total)
          </span>
          
          <button
            onClick={() => cambiarPagina(paginacion.page + 1)}
            disabled={paginacion.page >= paginacion.totalPages}
            className="btn-paginacion"
          >
            Siguiente →
          </button>
        </div>
      )}

      {/* Modal de confirmación */}
          {/* Modal de confirmación */}
          {mostrarModal && usuarioAEliminar && estadisticasUsuario && (
            <ConfirmDeleteModal
              usuario={usuarioAEliminar}
              estadisticas={estadisticasUsuario}
              onConfirm={confirmarEliminacion}
              onCancel={cancelarEliminacion}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPanel;
